import { ProductSupplierDoc, SupplierAdditionalContact } from '@gooduncles/gu-app-schema';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo } from 'react';
import { purchaseMethodToKr } from 'src/constants';

import { formatDateForAgCell, onCellValueChangedWithUpdate } from 'src/utils/ag-grid-util';

import useProductSuppliers from 'src/hooks/subscription/useProductSuppliers';
import { useTitle } from 'src/hooks/useTitle';

import AgCellAdditionalContacts from 'src/atomic-components/atoms/AgCell/AgCellAdditionalContacts';
import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import CreateSupplierModal from 'src/atomic-components/organisms/Modals/CreateSupplierModal';
import WoojooSoldOutSyncModal from 'src/atomic-components/organisms/Modals/WoojooSoldOutSyncModal';

const defaultColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const productSupplierPath = 'productSupplier';
const onCellValueChanged = onCellValueChangedWithUpdate<ProductSupplierDoc>('거래처 관리');

const SOLD_OUT_SUPPLIER_ID = 'fugBvrUFgCg2fOJOMeAN';

const AdditionalFeatureCell: FC = (params: any) => {
  const { data } = params ?? {};
  if (data?._id !== SOLD_OUT_SUPPLIER_ID) {
    return null;
  }
  return <WoojooSoldOutSyncModal />;
};

const ProductSupplierBoard: FC = () => {
  useTitle('거래처 관리');
  const { productSuppliers } = useProductSuppliers();

  const columnDefs: ColDef<ProductSupplierDoc>[] = useMemo(
    () => [
      {
        headerName: 'ID',
        field: '_id',
        hide: true,
      },
      {
        headerName: '생성일',
        field: '_timeCreate',
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
        hide: true,
      },
      {
        headerName: '수정일',
        field: '_timeUpdate',
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
        hide: true,
      },
      {
        headerName: '*상호명',
        field: 'name',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, productSupplierPath, params.data._id),
      },
      {
        headerName: '*그룹',
        field: 'groupId',
        width: 100,
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, productSupplierPath, params.data._id),
      },
      {
        headerName: '발주 채널',
        width: 120,
        field: 'orderChannel',
        valueFormatter: (params) => purchaseMethodToKr[params.value as keyof typeof purchaseMethodToKr] ?? '',
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: Object.keys(purchaseMethodToKr),
        },
        filterParams: {
          valueFormatter: (params: any) => {
            const value: string = params.value;
            return purchaseMethodToKr[value as keyof typeof purchaseMethodToKr] ?? '';
          },
        },
        onCellValueChanged: (params) => onCellValueChanged(params, productSupplierPath, params.data._id),
      },
      {
        headerName: '대표 연락처',
        field: 'primaryContact',
        editable: true,
        onCellValueChanged: (params) => onCellValueChanged(params, productSupplierPath, params.data._id),
      },
      {
        headerName: '대표자 성명',
        field: 'ownerName',
        width: 120,
        editable: true,
        onCellValueChanged: (params) =>
          onCellValueChanged(params, productSupplierPath, params.data._id, undefined, true, false, ''),
      },
      {
        headerName: '사업자 등록번호',
        field: 'businessNumber',
        editable: true,
        onCellValueChanged: (params) =>
          onCellValueChanged(params, productSupplierPath, params.data._id, undefined, true, false, ''),
      },
      {
        headerName: '추가 연락처',
        field: 'additionalContacts',
        valueFormatter: (params) =>
          params.value.map((contact: SupplierAdditionalContact) => `${contact.label}: ${contact.number}`).join('\n'),
        editable: true,
        cellEditorPopup: true,
        cellEditor: AgCellAdditionalContacts,
        onCellValueChanged: (params) =>
          onCellValueChanged(params, productSupplierPath, params.data._id, undefined, true, false, []),
        cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.5' },
        autoHeight: true,
      },
      {
        headerName: '사업장 주소',
        field: 'address',
        editable: true,
        onCellValueChanged: (params) =>
          onCellValueChanged(params, productSupplierPath, params.data._id, undefined, true, false, ''),
      },
      {
        headerName: '업태',
        field: 'businessType',
        editable: true,
        onCellValueChanged: (params) =>
          onCellValueChanged(params, productSupplierPath, params.data._id, undefined, true, false, ''),
      },
      {
        headerName: '종목',
        field: 'businessCategory',
        editable: true,
        onCellValueChanged: (params) =>
          onCellValueChanged(params, productSupplierPath, params.data._id, undefined, true, false, ''),
      },

      {
        headerName: '숨김 여부',
        field: 'hidden',
        editable: true,
        cellEditorPopup: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: {
          values: [true, false],
        },
        onCellValueChanged: (params) => onCellValueChanged(params, productSupplierPath, params.data._id),
      },
      {
        headerName: '액션',
        field: '_id',
        cellRenderer: DownloadRawDataCell,
      },
      {
        headerName: '추가 기능',
        field: '_id',
        cellRenderer: AdditionalFeatureCell,
      },
    ],
    [],
  );

  return (
    <div className='ag-theme-alpine height100 flexColumn'>
      <div className='flexRow flexSpaceBetween'>
        <CreateSupplierModal />
      </div>
      <AgGridReact
        rowData={productSuppliers}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        enableCellTextSelection={true}
        tooltipShowDelay={500}
      />
    </div>
  );
};

export default ProductSupplierBoard;
